/**
 * The icon a readmodel tile wears, BY WHAT IT COUNTS (2026-08-19).
 *
 * Extracted from `AlertWidget` when `count` needed the same thing — review item 8:
 * *"count items should have an icon on the right, like the Publications count on
 * the dashboard (the fixed one), each one should have its own icon."*
 *
 * ## Why the collection, and not the title or the severity
 *
 * Three candidates, and only one works:
 *
 *  - **severity** (what `alert` used before): three glyphs across every alert in the
 *    app, so four tiles in a row were three triangles and a circle. It encodes
 *    urgency, which the COLOUR already carries.
 *  - **the title**: author-written prose. There is no honest mapping from
 *    "Publicaciones pausadas hace más de 30 días" to a glyph.
 *  - **the collection**: the one machine-readable fact a tile knows about its own
 *    subject. `KeyIndicators` — the hand-written dashboard component these tiles
 *    replace — picks per-subject icons exactly this way.
 *
 * ⚠️ The fallback is the CALLER's, not a generic dot here. An `alert` with an
 * unmapped collection should fall back to its severity glyph (it has one); a
 * `count` has no severity and falls back to a neutral subject mark. Returning
 * `undefined` lets each decide.
 */
import type { ReactNode } from 'react';
import {
  IconBell,
  IconBox,
  IconGitCompare,
  IconHash,
  IconMessage,
  IconRoute2,
  IconRuler,
  IconShoppingCart,
  IconTag,
  IconTimeline
} from '@tabler/icons-react';

type IconComponent = typeof IconTag;

const COLLECTION_ICON: Record<string, IconComponent> = {
  items_publishing_publications_readmodel: IconTag,
  items_publishing_inventory_readmodel: IconBox,
  items_publishing_publication_drift_episodes_readmodel: IconGitCompare,
  items_publishing_size_guide_readmodel: IconRuler,
  batch_processes_readmodel: IconTimeline,
  crm_questions_readmodel: IconMessage,
  sales_orders_readmodel: IconShoppingCart,
  automation_rule_executions_readmodel: IconRoute2,
  notifications_notifications_readmodel: IconBell
};

/** `undefined` when the collection is unmapped — the caller picks the fallback. */
export const collectionIconFor = ( collection: string | undefined ): IconComponent | undefined =>
  ( collection ? COLLECTION_ICON[collection] : undefined );

/**
 * A rendered subject icon, with a NEUTRAL fallback.
 *
 * `IconHash` for "a count of something we have no specific mark for" — it reads as
 * a quantity, which is true of every readmodel count, rather than as a wrong
 * subject. Used by `count`, which has no severity glyph to fall back to.
 */
export const subjectIcon = ( collection: string | undefined, size = 18 ): ReactNode => {
  const Icon = collectionIconFor( collection ) ?? IconHash;
  return <Icon size={size} />;
};
